/**
@format
@flow local-strict
*/
import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { Jumbo, Heading, Body } from '@ziynx/components/typography';
import { ContentContainer } from '@ziynx/components/layout';
import Constants from '@ziynx/components/layout/Constants';
import { withAppManager } from '@ziynx/components/utilities';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import type { ViewportType } from '@ziynx/flow-types/viewports';
import termsOfServiceData from '../pageData/termsOfServiceData';

type Props = $ReadOnly<{|

|}>;

const calcWrapperPadding = (viewport: ViewportType) => {
  switch (viewport) {
  case 'mobile':
  case 'tablet':
    return '0px 16px';
  case 'desktop':
  case 'desktopSmall':
  default:
    return '0px 40px';
  }
};

const calcHeaderContentPadding = (viewport: ViewportType) => {
  switch (viewport) {
  case 'mobile':
    return '64px';
  case 'tablet':
  case 'desktopSmall':
    return '96px';
  case 'desktop':
  default:
    return '120px';
  }
};

const UserAgreementContainer = styled.div`
  padding: ${({ viewport }) => calcWrapperPadding(viewport)};
  width: 100%;
`;

const HeadingContainer = styled.div`
  padding-bottom: ${({ viewport }) => calcHeaderContentPadding(viewport)};
`;

const BodyWrapper = styled.div`
  padding-bottom: ${({ paddingBottom }) => paddingBottom};
`;

const StyledJumbo = styled(Jumbo)`
  padding-bottom: ${Constants.defaultPadding}px;
`;
const StyledHeader = styled(Heading)`
  padding-bottom: ${Constants.defaultPadding}px;
`;

const SubHeadingWrapper = styled.div`
  padding-bottom: 24px;
`;

const renderBody = (
  data: $ReadOnlyArray<{children: string}>,
  isDesktop: boolean,
  isDesktopSmall: boolean,
) => data.map((text, index) => {
  const keyIndex = `body-${index}`;
  const paddingBottom = index === data.length - 1 ? 0 : '28px';
  return (
    <BodyWrapper paddingBottom={paddingBottom}>
      <Body
        large={isDesktop || isDesktopSmall}
        medium={!isDesktop && !isDesktopSmall}
        key={keyIndex}
      >
        {text.children}
      </Body>
    </BodyWrapper>
  );
});

const TermsOfService = (props: ManagedProps<Props>): React$Node => {
  const { viewport } = props;

  const isDesktop = viewport === 'desktop';
  const isDesktopSmall = viewport === 'desktopSmall';

  return (
    <>
      <Helmet>
        <title>Terms | ZIYNX</title>
      </Helmet>
      <ContentContainer offsetTop={96}>
        <UserAgreementContainer viewport={viewport}>
          <HeadingContainer viewport={viewport}>
            {!isDesktop && <StyledHeader large>Terms of service</StyledHeader>}
            {isDesktop && <StyledJumbo small>Terms of service</StyledJumbo>}
            <SubHeadingWrapper>
              <Body
                extraLarge={isDesktop}
                large={!isDesktop}
              >
                Read our terms below to learn more about your
                <br />
                rights and responsibilities as a ZIYNX user.
              </Body>
            </SubHeadingWrapper>
            <Body
              large={isDesktop || isDesktopSmall}
              medium={!isDesktop && !isDesktopSmall}
            >
              Updated: April 14, 2021
            </Body>
          </HeadingContainer>
          {renderBody(termsOfServiceData, isDesktop, isDesktopSmall)}
        </UserAgreementContainer>
      </ContentContainer>
    </>
  );
};

TermsOfService.defaultProps = {
  viewport: 'desktop',
};

export default (withAppManager(TermsOfService): React$ComponentType<
  React$Config<ManagedProps<Props>, {viewport: ViewportType}>
>);
